/**
 * Стандартизированные константы для всего приложения
 * Все таймауты, лимиты и другие конфигурационные значения
 */

// Логирование: в production по умолчанию выключено (чтобы не засорять консоль и не раскрывать детали).
// В dev включено; можно переопределить через `VITE_UI_DEBUG=true|false`.
export const UI_DEBUG: boolean =
  typeof __ALGOSYS_DEBUG__ !== 'undefined' ? Boolean(__ALGOSYS_DEBUG__) : false;

export const logDebug = (...args: unknown[]) => {
  // eslint-disable-next-line no-console -- допустимо для отладочного лога при UI_DEBUG
  if (UI_DEBUG) console.debug(...args);
};

export const logWarn = (...args: unknown[]) => {
  if (UI_DEBUG) console.warn(...args);
};

export const logError = (...args: unknown[]) => {
  if (UI_DEBUG) console.error(...args);
};

// Таймауты для сетевых операций
export const TIMEOUTS = {
  // Быстрые операции (WS ping, health checks)
  FAST: 5000, // 5 секунд

  // Обычные API запросы
  NORMAL: 15000, // 15 секунд

  // Долгие операции (расчеты индикаторов, бэктест)
  LONG: 120000, // 2 минуты

  // Старт/создание агента: lazy-restore, warmup, Session START
  AGENT_START: 120000, // 2 минуты — не меньше proxy_read_timeout /api на nginx

  // AI codegen (OpenAI + validate + smoke) и quick-save с валидацией плагина
  AI: 120000, // 2 минуты — не меньше TRADING_SPECS_AI_REQUEST_TIMEOUT_SEC на API
  QUICK_SAVE: 90000, // 90 секунд

  // WebSocket операции
  WS_PING: 30000, // 30 секунд
  WS_RECONNECT_BASE: 1000, // 1 секунда (экспоненциальная задержка)
  WS_RECONNECT_MAX: 30000, // 30 секунд максимум
  WS_LIVE_LAG: 3000, // порог лагов для статуса live/stalled

  // React Query кэширование
  CACHE_SHORT: 5000, // 5 секунд (тикеры)
  CACHE_NORMAL: 60000, // 1 минута (рыночные данные)
  CACHE_LONG: 300000, // 5 минут (исторические данные)
} as const;

// Лимиты данных
export const LIMITS = {
  MAX_BARS: 12000, // максимум баров в графике = окно индикаторов агента (DEFAULT_INDICATOR_WINDOW_BARS)
  // Окно пересчёта индикаторов графика = окно агента/бэктеста (12000). На 2000 барах
  // рекурсивные индикаторы (iTrend на swing-шкале) не успевали сойтись, и график
  // показывал не тот рубильник, что у агента.
  INDICATOR_WINDOW_BARS: 12000,
  MAX_RECONNECT_ATTEMPTS: 10, // максимум попыток переподключения WS
  DEFAULT_BAR_LIMIT: 100, // лимит баров по умолчанию для API
  /** Согласовано с apps/api/src/app/ws/protocol.py (bars: max_messages). */
  BARS_RECOVERY_MAX_MESSAGES: 500,
  /** Согласовано с apps/api/src/app/ws/container.py RecoveryBuffer max_age_seconds. */
  BARS_RECOVERY_MAX_AGE_SEC: 300,
} as const;

// Дебаунс и троттлинг
export const DEBOUNCE = {
  INDICATORS_CALC: 300, // дебаунс расчета индикаторов (мс)
  CHART_UPDATE: 100, // троттлинг обновлений графика (мс)
} as const;

// WebSocket каналы
export const WS_CHANNELS = {
  TICKS: (symbol: string) => `ticks:${symbol.toLowerCase()}`,
  // interval_id может содержать ":" (T:5m / X:500t) — не нормализуем регистр, только symbol.
  BARS: (symbol: string, intervalId: string) => `bars:${symbol.toLowerCase()}:${normalizeIntervalId(String(intervalId ?? ''))}`,
  TICKER: (symbol: string) => `ticker:${symbol.toLowerCase()}`,
  /** Канон L1 top-of-book (отдельно от ticker display). */
  TOP_OF_BOOK: (symbol: string) => `top_of_book:${symbol.toLowerCase()}`,
  // Канон service-channel: trading:signal:<agent_id> / trading:execution:<agent_id>
  TRADING_SIGNAL: (agentId: string) => `trading:signal:${(agentId ?? '').replace(/:/g, '_')}`,
  TRADING_EXECUTION: (agentId: string) => `trading:execution:${(agentId ?? '').replace(/:/g, '_')}`,
  // Портфолио и позиции (agentId без ":" для единого формата канала)
  PORTFOLIO: (agentId: string) => `portfolio:${(agentId ?? '').replace(/:/g, '_')}`,
  POSITIONS: (agentId: string) => `positions:${(agentId ?? '').replace(/:/g, '_')}`,
  ORDERS: (agentId: string) => `orders:${(agentId ?? '').replace(/:/g, '_')}`,
  ORDER: (orderId: string) => `order:${(orderId ?? '').replace(/:/g, '_')}`,
  // Агенты (agentId без ":"; подписка и emit по канону agent:symbol)
  AGENT_STATUS: (agentId: string) => `agent:${(agentId ?? '').replace(/:/g, '_')}:status`,
  AGENT_STATS: (agentId: string) => `agent:${(agentId ?? '').replace(/:/g, '_')}:stats`,
  // Системные
  SYSTEM_HEALTH: () => 'system.health',
} as const;

// Стандартизированные типы ошибок
export const ERROR_TYPES = {
  NETWORK: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT_ERROR',
  VALIDATION: 'VALIDATION_ERROR',
  UNAUTHORIZED: 'UNAUTHORIZED_ERROR',
  NOT_FOUND: 'NOT_FOUND_ERROR',
  SERVER: 'SERVER_ERROR',
  UNKNOWN: 'UNKNOWN_ERROR',
} as const;

export const ERROR_MESSAGES = {
  [ERROR_TYPES.NETWORK]: 'Network connection error',
  [ERROR_TYPES.TIMEOUT]: 'Request timeout',
  [ERROR_TYPES.VALIDATION]: 'Invalid data provided',
  [ERROR_TYPES.UNAUTHORIZED]: 'Authentication required',
  [ERROR_TYPES.NOT_FOUND]: 'Resource not found',
  [ERROR_TYPES.SERVER]: 'Server error',
  [ERROR_TYPES.UNKNOWN]: 'An unexpected error occurred',
} as const;

// Утилиты для обработки ошибок
type UnknownRecord = Record<string, unknown>;
const isRecord = (v: unknown): v is UnknownRecord => !!v && typeof v === 'object' && !Array.isArray(v);

export const getErrorType = (error: unknown): string => {
  const rec = isRecord(error) ? (error as UnknownRecord) : null;
  const code = rec ? rec.code : undefined;
  const msg = rec && typeof rec.message === 'string' ? rec.message : '';

  if (code === 'ECONNABORTED' || msg.includes('timeout')) {
    return ERROR_TYPES.TIMEOUT;
  }
  if (rec && isRecord(rec.response)) {
    const status = Number((rec.response as UnknownRecord).status);
    if (status === 401 || status === 403) return ERROR_TYPES.UNAUTHORIZED;
    if (status === 404) return ERROR_TYPES.NOT_FOUND;
    if (status >= 500) return ERROR_TYPES.SERVER;
    if (status >= 400) return ERROR_TYPES.VALIDATION;
  }
  if (!rec || !('response' in rec) || !rec.response) {
    return ERROR_TYPES.NETWORK;
  }
  return ERROR_TYPES.UNKNOWN;
};

export const getErrorMessage = (error: unknown): string => {
  const errorType = getErrorType(error);
  const errorKey = errorType as keyof typeof ERROR_MESSAGES;
  return ERROR_MESSAGES[errorKey] || ERROR_MESSAGES[ERROR_TYPES.UNKNOWN];
};

/** Сетевые ошибки (API недоступен) — не ретраить агрессивно, не засорять консоль. */
export const isNetworkError = (error: unknown): boolean => {
  if (getErrorType(error) === ERROR_TYPES.NETWORK) return true;
  const o = isRecord(error) ? (error as Record<string, unknown>) : {};
  const msg = String(o.message ?? '');
  const code = o.code as string | undefined;
  return (
    msg.includes('ERR_CONNECTION_RESET') ||
    msg.includes('ERR_SOCKET_NOT_CONNECTED') ||
    msg.includes('Network Error') ||
    code === 'ERR_NETWORK'
  );
};

// Валидационные функции
export const validateSymbol = (symbol: string): boolean => {
  return typeof symbol === 'string' && symbol.length > 0 && symbol.length <= 20 && /^[A-Z0-9]+$/.test(symbol.toUpperCase());
};

export const validateInterval = (interval: string): boolean => {
  if (typeof interval !== 'string') return false;
  const value = interval.trim().toLowerCase();
  if (!value) return false;

  // Канон: T:<int><s|m|h|d|w>, X:<int>t
  const canonTime = value.match(/^t:(\d+)([smhdw])$/);
  if (canonTime) {
    const n = Number(canonTime[1]);
    return Number.isInteger(n) && n > 0;
  }
  const canonTick = value.match(/^x:(\d+)t$/);
  if (canonTick) {
    const n = Number(canonTick[1]);
    return Number.isInteger(n) && n > 0;
  }

  return false;
};

/** Единый нормализатор symbol для chart key (boundary). */
export const normalizeChartSymbol = (s: string): string => (s ?? '').trim().toLowerCase();

/**
 * Нормализовать interval к каноническому interval_id (P2 boundary — VERIFY_DATA_FLOW §4).
 *
 * Канон data-plane: T:/X: только.
 * - time: T:<int><unit>  (unit ∈ s,m,h,d,w)
 * - tick: X:<int>t
 */
export const normalizeIntervalId = (interval: string): string => {
  const raw = String(interval ?? '').trim();
  if (!raw) return raw;
  const lower = raw.toLowerCase();

  // Уже канон
  const canonTime = lower.match(/^t:(\d+)([smhdw])$/);
  if (canonTime) return `T:${Number(canonTime[1])}${canonTime[2]}`;
  const canonTick = lower.match(/^x:(\d+)t$/);
  if (canonTick) return `X:${Number(canonTick[1])}t`;

  // Legacy time interval (e.g. 1m, 5h) -> canonical T:<n><unit>
  const legacyTime = lower.match(/^(\d+)([smhdw])$/);
  if (legacyTime) return `T:${Number(legacyTime[1])}${legacyTime[2]}`;

  // Legacy tick interval (e.g. 200t) -> canonical X:<n>t
  const legacyTick = lower.match(/^(\d+)t$/);
  if (legacyTick) return `X:${Number(legacyTick[1])}t`;

  // Unknown format: return as-is so validators can reject deterministically.
  return raw;
};

export const validateLimit = (limit?: number): boolean => {
  if (limit === undefined) return true;
  return Number.isInteger(limit) && limit > 0 && limit <= LIMITS.MAX_BARS;
};

export const validateDateRange = (startTime?: Date, endTime?: Date): boolean => {
  if (!startTime && !endTime) return true;
  if (startTime && endTime) {
    return startTime < endTime;
  }
  return true;
};

// Комплексная валидация параметров
export const validateMarketDataParams = (symbol: string, options?: {
  interval?: string;
  limit?: number;
  startTime?: Date;
  endTime?: Date;
}): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!validateSymbol(symbol)) {
    errors.push('Invalid symbol format');
  }

  if (options?.interval && !validateInterval(options.interval)) {
    errors.push('Invalid interval format');
  }

  if (!validateLimit(options?.limit)) {
    errors.push('Invalid limit value');
  }

  if (!validateDateRange(options?.startTime, options?.endTime)) {
    errors.push('Invalid date range');
  }

  return { isValid: errors.length === 0, errors };
};

// Унифицированная функция валидации API ответов с типизацией
export const validateApiResponse = <T>(
  data: unknown,
  validator: (data: unknown) => boolean,
  errorMessage?: string
): data is T => {
  const isValid = validator(data);
  if (!isValid && errorMessage) {
    logWarn(errorMessage, data);
  }
  return isValid;
};

// Валидация ответов API (legacy функции)
export const validateMarketDataResponse = (data: unknown): boolean => {
  // Мягкая валидация контейнера: детальная нормализация делается на этапе маппинга
  if (!isRecord(data)) return false;
  if (!Array.isArray((data as UnknownRecord).bars)) return false;
  // Поля symbol/interval/count могут отсутствовать или быть в альтернативном виде — не валидируем строго
  return true;
};

export const validateSubscriptionResponse = (data: unknown): boolean => {
  return isRecord(data) && typeof (data as UnknownRecord).success === 'boolean';
};

export const validateTickerResponse = (data: unknown): boolean => {
  if (!isRecord(data)) return false;
  const rec = data as UnknownRecord;
  const symbol = rec.symbol;
  const price = rec.price;
  return (
    typeof symbol === 'string' &&
    validateSymbol(symbol) &&
    Number.isFinite(typeof price === 'number' ? price : Number(price)) &&
    (typeof price === 'number' ? price : Number(price)) >= 0
  );
};

// Валидация AgentResponse
export const validateAgentResponse = (data: unknown): boolean => {
  if (!isRecord(data)) return false;
  const rec = data as UnknownRecord;
  if (typeof rec.id !== 'string' || rec.id.length === 0) return false;
  if (typeof rec.symbol !== 'string') return false;
  if (typeof rec.interval !== 'string') return false;
  if (typeof rec.isRunning !== 'boolean') return false;
  return true;
};

// Валидация BrokerInstance
export const validateBrokerInstance = (data: unknown): boolean => {
  if (!isRecord(data)) return false;
  const rec = data as UnknownRecord;
  if (typeof rec.broker_id !== 'string' || rec.broker_id.length === 0) return false;
  if (typeof rec.type !== 'string') return false;
  if (typeof rec.status !== 'string') return false;
  const validTypes = ['paper', 'futures_paper', 'binance_futures'];
  if (!validTypes.includes(rec.type)) return false;
  return true;
};

// Валидация OrderStatus
export const validateOrderStatus = (data: unknown): boolean => {
  if (!isRecord(data)) return false;
  const rec = data as UnknownRecord;
  if (typeof rec.order_id !== 'string') return false;
  if (typeof rec.symbol !== 'string') return false;
  if (typeof rec.side !== 'string' || !['BUY', 'SELL'].includes(rec.side)) return false;
  if (typeof rec.status !== 'string') return false;
  return true;
};

// Валидация массива OrderStatus
export const validateOrderStatusArray = (data: unknown): boolean => {
  if (!Array.isArray(data)) return false;
  return data.every((order) => validateOrderStatus(order));
};

// Валидация AccountInfo
export const validateAccountInfo = (data: unknown): boolean => {
  if (!isRecord(data)) return false;
  if (!Array.isArray((data as UnknownRecord).balances)) return false;
  return true;
};

// Валидация Position
export const validatePosition = (data: unknown): boolean => {
  if (!isRecord(data)) return false;
  const rec = data as UnknownRecord;
  if (typeof rec.symbol !== 'string') return false;
  const positionSide = rec.position_side ?? rec.positionSide;
  if (typeof positionSide !== 'string') return false;
  return true;
};

// Валидация массива Position
export const validatePositionArray = (data: unknown): boolean => {
  if (!Array.isArray(data)) return false;
  return data.every((position) => validatePosition(position));
};

// Единая функция маппинга данных в BarData
// Маппинг баров перенесён в shared/model/bars.ts

// Типы данных
export type TimeoutType = keyof typeof TIMEOUTS;
export type LimitType = keyof typeof LIMITS;
export type DebounceType = keyof typeof DEBOUNCE;
export type ErrorType = typeof ERROR_TYPES[keyof typeof ERROR_TYPES];
